// dependencies
import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import {
	MAX_NUMBER_QUESTION_OPTIONS,
	SurveyMultipleChoiceOptionSchema,
	SurveyQuestionSchema,
	SurveyRatingScaleOptionSchema,
} from "@performyard/models";
import { FormHandle } from "@performyard/py-components";

// components
import ComponentLoader from "../../../../components/Spinner/ComponentLoader";
import Navigation from "./components/Navigation";
import SurveyDetailsModals from "./components/SurveyDetailsModals";

import { SurveyBuilder, SurveyParticipants, SurveyPreview, SurveyResults } from "./pages";
import { useActions as useSurveyBuilderActions } from "./pages/SurveyBuilder/store/store";
import {
	useActions as useSurveyParticipantsActions,
	useSetters as useSurveyParticipantsSetters,
} from "./pages/SurveyParticipants/store/store";

import { useSetters as useSurveyDetailsSetters } from "./store/store";

// hooks
import useSearchQueryParams from "../../../../hooks/useSearchQueryParams";
import { useSurvey } from "./hooks/useSurvey";

// components
import ErrorPage from "../../../Error";

export const searchParamsSchema = z.object({
	tab: z.enum(["build", "results", "preview", "participants"]).optional(),
});

export const surveyBuilderSchema = z.object({
	questions: z.array(
		SurveyQuestionSchema.extend({
			_id: z.string(),
			options: z
				.array(
					z.union([
						SurveyMultipleChoiceOptionSchema.extend({
							_id: z.string(),
						}),
						SurveyRatingScaleOptionSchema.extend({
							_id: z.string(),
						}),
					])
				)
				.max(MAX_NUMBER_QUESTION_OPTIONS)
				.optional()
				.nullish(),
		}).superRefine((data, ctx) => {
			// for instructions questions, we just need a text OR subtext, else we require a text
			if (data.type === "instructions") {
				if (!data.text?.trim() && !data?.subtitle?.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Instructions must have a title or subtitle",
						path: ["text"],
					});
				}
			} else {
				if (!data.text?.trim()) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "This is a required field",
						path: ["text"],
					});
				}
			}

			if (data.type === "multiple-choice" || data.type === "multiple-select") {
				if (!data.options?.length) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Multiple choice questions must have at least one option",
						path: ["options"],
					});
				}
			}

			if (data.type === "rating-scale") {
				if (!data.options?.length) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Rating scale questions must have at least one option",
						path: ["options"],
					});
				}
			}
		})
	),
});

export type TformRef = FormHandle<typeof surveyBuilderSchema>;

const SurveyDetails: React.FC = () => {
	const params = useParams();
	const { queryParams: searchParams, handleQueryChange: setSearchParams } = useSearchQueryParams({
		schema: searchParamsSchema,
	});
	const { surveyId = "" } = params;
	const tab = searchParams.tab;

	// store
	const { survey, storeSurvey, isLoading: isLoadingSurvey } = useSurvey({ surveyId });
	const surveyBuilderActions = useSurveyBuilderActions();
	const surveyDetailsSetters = useSurveyDetailsSetters();
	const surveyParticipantsSetters = useSurveyParticipantsSetters();
	const surveyParticipantsActions = useSurveyParticipantsActions();

	const formRef = useRef<TformRef>(null);

	useEffect(() => {
		return () => {
			surveyBuilderActions.resetStore();
		};
	}, []);

	useEffect(() => {
		if (survey) {
			storeSurvey(survey);
		}
	}, [JSON.stringify(survey)]);

	useEffect(() => {
		surveyDetailsSetters.setModal(null);
		if (tab !== "participants") {
			surveyParticipantsSetters.setSurveyAccess(
				survey?.participantSelection?.enabled ? "selected-employees" : "all-employees"
			);
			// Reset the participants store when navigating away from the participants tab
			surveyParticipantsActions.resetStore();
		}
	}, [tab, survey?.participantSelection?.enabled]);

	const content = useMemo(() => {
		if (!survey) {
			return null;
		}
		switch (tab) {
			case "build":
				return <SurveyBuilder survey={survey} formRef={formRef} />;
			case "results":
				return <SurveyResults surveyId={surveyId} />;
			case "preview":
				return <SurveyPreview survey={survey} />;
			case "participants":
				return <SurveyParticipants surveyId={surveyId} />;
			default:
				setSearchParams({ tab: "results" });
				return <SurveyResults surveyId={surveyId} />;
		}
	}, [tab, JSON.stringify(survey)]);

	if (isLoadingSurvey) {
		return <ComponentLoader />;
	}

	if (!survey && !isLoadingSurvey) {
		return <ErrorPage code={404} />;
	}

	return (
		<div className="flex flex-1 flex-col">
			<Navigation
				surveyName={survey?.name || ""}
				surveyId={surveyId}
				onPublishClick={() => {
					formRef.current?.submit({ action: "publish-survey" });
				}}
			/>
			{content}
			<SurveyDetailsModals
				surveyId={surveyId}
				surveyName={survey?.name || ""}
				resetForm={() => {
					formRef.current?.reset();
				}}
			/>
		</div>
	);
};

export default SurveyDetails;
